import { useMemo, useState } from 'react';
import { Search, AlertTriangle } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { UsersTable } from '../components/tables/UsersTable';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { PermissionsResult, UsersResult } from '../types/org.types';

type Filter = 'all' | 'active' | 'inactive' | 'no-login-90';

// Data source: the live audit store by default, which since TYR-216 is the
// client Hub alone. A caller that already holds an audit, such as the stored
// client scan on the internal lead page, passes it in.
// Passing `data` at all, null included, switches the page to scan mode, where
// NO slice is ever read from the store. A category the scan could not read is
// stored as null, so null is a captured answer ("not available"), never a
// reason to reach for the live org's numbers.
export function UsersPage({
  data,
  permissionsData,
}: { data?: UsersResult | null; permissionsData?: PermissionsResult | null } = {}) {
  const storeUsers = useOrgStore((s) => s.audit.users);
  const storePermissions = useOrgStore((s) => s.audit.permissions);
  const scanMode = data !== undefined;
  const users = scanMode ? data : storeUsers;
  const permissions = scanMode ? permissionsData : storePermissions;
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    if (!users) return [];
    const ninetyDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 90;
    return users.users.filter((u) => {
      if (filter === 'active' && !u.isActive) return false;
      if (filter === 'inactive' && u.isActive) return false;
      if (filter === 'no-login-90') {
        if (!u.isActive) return false;
        if (u.lastLoginDate && new Date(u.lastLoginDate).getTime() >= ninetyDaysAgo) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const haystack = `${u.name} ${u.email ?? ''} ${u.profileName ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [users, filter, search]);

  if (!users) return <EmptyState title="No user data. Reconnect to scan again." />;

  const t = users.totals;
  const utilPct = t.licensed > 0 ? Math.round((t.active / t.licensed) * 100) : 0;
  const adminProfiles = (permissions?.profiles ?? []).filter((p) => p.isAdmin && p.userCount > 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-adopt mb-1">Adopt</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">User & Permission Analysis</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Users" value={t.total} accent="adopt" />
        <StatCard label="Active" value={t.active} accent="grow" />
        <StatCard
          label="Inactive (Licensed)"
          value={t.inactiveLicensed}
          accent={t.inactiveLicensed > 5 ? 'sunset' : 'adopt'}
          hint="Cost exposure"
        />
        <StatCard
          label="No login >90 days"
          value={t.noLoginIn90Days}
          accent={t.noLoginIn90Days > 5 ? 'sunrise' : 'adopt'}
        />
      </div>

      <div className="bg-surface-1 border border-border rounded-lg p-5">
        <div className="flex items-center justify-between text-xs text-neutral-500 mb-2">
          <span className="uppercase tracking-widest">License Utilisation</span>
          <span className="text-neutral-400 tabular-nums">
            {utilPct}% ({t.active}/{t.licensed})
          </span>
        </div>
        <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${utilPct}%`,
              background: utilPct >= 70 ? '#3FA96B' : utilPct >= 50 ? '#C7851A' : '#D64545',
            }}
          />
        </div>
      </div>

      {t.inactiveLicensed > 0 && (
        <div className="flex items-start gap-3 bg-sunset/10 border border-sunset/30 rounded-md p-3 text-sm text-sunset">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            {t.inactiveLicensed} user{t.inactiveLicensed > 1 ? 's' : ''} hold a paid licence but are inactive.
          </span>
        </div>
      )}

      {/* Admin profiles come from the permissions audit. A scan that failed to
          read it must say so, not render an absence that reads as "none".
          Live sessions keep the behaviour they had. */}
      {scanMode && !permissions && (
        <div className="text-sm text-neutral-500">
          Admin-level profiles were not captured, so that check is unavailable.
        </div>
      )}

      {adminProfiles.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
            High-Risk Profiles (Admin-level access)
          </div>
          <div className="space-y-1.5">
            {adminProfiles.map((p) => (
              <div
                key={p.name}
                className="flex items-center justify-between bg-surface-2 border border-border rounded-md px-4 py-2 text-sm"
              >
                <span className="text-crux-light">{p.name}</span>
                <span className="text-neutral-400">
                  {p.userCount} user{p.userCount === 1 ? '' : 's'}
                  {p.userCount > 5 ? <span className="text-sunset ml-2">(review)</span> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <CollapsibleSection
        title="Full user list"
        meta={`${rows.length} of ${users.users.length} shown`}
        defaultOpen={rows.length <= 20}
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users…"
              className="bg-surface-2 border border-border rounded-md pl-9 pr-3 py-2 text-sm text-crux-light placeholder:text-neutral-600 focus:outline-none focus:border-adopt w-64"
            />
          </div>
          <div className="flex gap-1">
            {(['all', 'active', 'inactive', 'no-login-90'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  filter === f
                    ? 'bg-adopt/15 border-adopt/40 text-adopt'
                    : 'bg-surface-2 border-border text-neutral-400 hover:text-crux-light'
                }`}
              >
                {f === 'no-login-90' ? 'No login 90d' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <UsersTable rows={rows} />
      </CollapsibleSection>
    </div>
  );
}
